import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProjectList.module.scss';
import {IconTemplate, IconTrash} from '@tabler/icons-react';
import ProjectCardView from './ProjectCardView';
import { ProjectType } from '../../api/model';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';

export interface ProjectListProject {
  id: string;
  title: string;
  description: string;
  logoUrl: string;
  type: ProjectType;
  method?: 'AGILE' | 'CLASSIC' | 'HYBRID';
  status: 'Completed' | 'Delayed' | 'In Progress' | 'Place holder';
  teamMembers: Array<{
    id: string;
    name: string;
    avatarUrl: string;
  }>;
  lastEdit: {
    date: string;
    editor: string;
  };
  progress: number;
  startDate?: string;
  endDate?: string;
  milestone?: string;
}

interface ProjectListProps {
  projects: ProjectListProject[];
  itemsPerPage?: number;
  viewType?: 'list' | 'card';
  onProjectClick?: (project: any) => void;
  onProjectDelete?: (projectId: string) => void;
  onProjectTemplateCreate?: (project: ProjectListProject) => void;
}


// project list component for dashboard page set current page, selected items, select all, and items per page with state management
const ProjectList: React.FC<ProjectListProps> = ({
                                                   projects,
                                                   itemsPerPage = 8,
                                                   viewType = 'list',
                                                   onProjectClick,
                                                   onProjectDelete,
                                                   onProjectTemplateCreate
                                                 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const { t } = useTranslation('project');

  const getMethodBadgeClass = (method?: string) => {
    switch (method) {
      case 'AGILE':
        return styles.agileMethodBadge;
      case 'CLASSIC':
        return styles.classicMethodBadge;
      case 'HYBRID':
        return styles.hybridMethodBadge;
      default:
        return styles.agileMethodBadge;
    }
  };

  const formatMethod = (method?: string): string => {
    switch (method) {
      case 'AGILE':
        return t('list.method.agile');
      case 'CLASSIC':
        return t('list.method.classic');
      case 'HYBRID':
        return t('list.method.hybrid');
      default:
        return t('list.method.na');
    }
  };

  const getStatusBadgeClass = (progress: number) => {
    if (progress === 0) {
      return styles.statusPlaceholder;
    } else if (progress === 100) {
      return styles.statusCompleted;
    } else {
      return styles.statusInProgress;
    }
  };

  const getStatusText = (progress: number): string => {
    if (progress === 0) {
      return t('list.status.planned');
    } else if (progress === 100) {
      return t('list.status.done');
    } else {
      return t('list.status.inProgress');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).replace(',', ' |');
  };

  const handleDeleteClick = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setProjectToDelete(projectId);
    setIsDeleteDialogOpen(true);
  };

  const handleTemplateClick = (project: ProjectListProject, event: React.MouseEvent) => {
    event.stopPropagation();
    onProjectTemplateCreate?.(project);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete && onProjectDelete) {
      onProjectDelete(projectToDelete);
    }
    setProjectToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  // Pagination logic
  let paginatedProjects: ProjectListProps['projects'] = [];
  let totalPages = 1;
  const cardsPerPage = viewType === 'card' ? 9 : itemsPerPage;
  
  totalPages = Math.ceil(projects.length / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  paginatedProjects = projects.slice(startIndex, startIndex + cardsPerPage);

  if (viewType === 'card') {
    return (
      <div className={styles.cardViewContainer}>
        <div className={styles.cardGrid}>
          {paginatedProjects.map((project) => (
            <ProjectCardView 
              key={project.id}
              project={project} 
              onDelete={handleDeleteClick}
              onTemplateCreate={handleTemplateClick}
              onClick={() => onProjectClick?.(project)}
            />
          ))}
        </div>
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            {t('list.pagination.previous')}
          </button>
          <div className={styles.paginationInfo}>
            {t('list.pagination.page', { current: currentPage, total: totalPages })}
          </div>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            {t('list.pagination.next')}
          </button>
        </div>
        <ConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title={t('list.deleteDialog.title')}
          message={t('list.deleteDialog.message')}
          confirmText={t('list.deleteDialog.confirm')}
          cancelText={t('list.deleteDialog.cancel')}
          type="danger"
        />
      </div>
    );
  }

  const tableHeight = 44 + (paginatedProjects.length * 72);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table} style={{ height: `${tableHeight}px` }}>
        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div className={styles.headerTitle}>
              <span>{t('list.headers.project')}</span>
            </div>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell} onClick={() => onProjectClick?.(project)} style={{ cursor: 'pointer' }}>
              <img src={project.logoUrl} alt={project.title} className={styles.projectLogo} />
              <div className={styles.projectInfo}>
                <span className={styles.projectTitle}>{project.title}</span>
                <span className={styles.projectDescription}>{project.description}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.method')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <div className={`${styles.badge} ${getMethodBadgeClass(project.method)}`}>
                {formatMethod(project.method)}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.characters')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <div className={styles.characterList}>
                {project.teamMembers.length > 0 ? (
                  <div className={styles.characterContainer}>
                    <div className={styles.characterNames}>
                      {project.teamMembers.slice(0, 2).map((member, index) => (
                        <React.Fragment key={member.id}>
                          <span
                            className={styles.characterName}  
                            title={member.name}
                          >
                            {member.name}
                          </span>
                          {index < Math.min(project.teamMembers.length, 2) - 1 && (
                            <span className={styles.characterSeparator}>, </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    {project.teamMembers.length > 2 && (
                      <span className={styles.characterMore}>
                        +{project.teamMembers.length - 2}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className={styles.noCharacters}>{t('list.noCharacters')}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.lastEdit')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <div className={styles.lastEdit}>
                <span className={styles.date}>{formatDate(project.lastEdit.date)}</span>
                <span className={styles.editor}>{project.lastEdit.editor}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.status')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <div className={`${styles.badge} ${getStatusBadgeClass(project.progress)}`}>
                {getStatusText(project.progress)}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.progress')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <div className={styles.progressBar}>
                <div className={styles.progressTrack}>
                  <div 
                    className={`${styles.progressFill} ${
                      project.progress === 100 ? styles.progressFull :
                      project.progress >= 50 ? styles.progressMedium :
                      styles.progressLow
                    }`}
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
                <span className={styles.progressText}>{project.progress}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.actions')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={`${styles.cell} ${styles.actionsCell}`}>
              <div className={styles.actionButtonGroup}>
                {onProjectTemplateCreate && (
                    <button
                        className={styles.templateButton}
                        onClick={(e) => handleTemplateClick(project, e)}
                        title={t('list.actions.createTemplate')}
                    >
                      <IconTemplate size={18}/>
                    </button>
                )}
                <button
                    className={styles.deleteButton}
                    onClick={(e) => handleDeleteClick(project.id, e)}
                    title={t('list.deleteDialog.confirm')}
                >
                  <IconTrash size={18}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.pagination}>
        <button 
          className={styles.paginationButton} 
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          {t('list.pagination.previous')}
        </button>
        <div className={styles.paginationInfo}>
          {t('list.pagination.page', { current: currentPage, total: totalPages })}
        </div>
        <button 
          className={styles.paginationButton}
          onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          {t('list.pagination.next')}
        </button>
      </div>
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('list.deleteDialog.title')}
        message={t('list.deleteDialog.message')}
        confirmText={t('list.deleteDialog.confirm')}
        cancelText={t('list.deleteDialog.cancel')}
        type="danger"
      />
    </div>
  );
};

export default ProjectList; 
